import React from 'react';
import { BudgetProposalWithResourcesT } from 'routes/BudgetModule/config/types';
import { StyMultiStepModal } from '../../shared/StyMultiStepModal';
import {
    useGetInscriptionOptionsQuery,
    useInitInscriptions,
    useGetInitInscriptionsFields,
} from './hooks';
import FirstStep from './steps/FirstStep';
import SecondStep from './steps/SecondStep';
import ThirdStep from './steps/ThirdStep';
import FourthStep from './steps/FourthStep';
import FifthStep from './steps/FifthStep';
import { InscriptionSource } from './type';
import { trackEventConfirmInitializeInscriptions } from '../trackersHelpers';

type Props = {
    isModalShown: boolean;
    setShowModal: DispatchState<boolean>;
    budgetProposals: BudgetProposalWithResourcesT[];
};

export default function InitInscriptionsModal({
    isModalShown,
    setShowModal,
    budgetProposals,
}: Props): React.ReactElement {
    const { data: options } = useGetInscriptionOptionsQuery();
    const {
        year,
        setYear,
        availableYears,
        inscriptionSteps,
        setInscriptionSteps,
        availableSteps,
        selectedBudgetProposals,
        setSelectedBudgetProposals,
        resourceSoftwareIds,
        setResourceSoftwareIds,
        softwareConfig,
        setSoftwareConfig,
        availableSoftwaresConfigs,
        existingInscriptionsAction,
        setExistingInscriptionsAction,
        availableExistingInscriptionsActions,
        resetAmountAndCommentAction,
        setResetAmountAndCommentAction,
        availableResetAmountAndCommentActions,
        commentColumn,
        setCommentColumn,
        availableCommentColumns,
        confirmationIsValid,
        setConfirmationIsValid,
        availableInscriptionSources,
        inscriptionSource,
        setInscriptionSource,
        availablePhases,
        phase,
        setPhase,
        availableKeepProjectActions,
        keepProjectAction,
        setKeepProjectAction,
    } = useGetInitInscriptionsFields(options);

    const { mutate: initInscriptions } = useInitInscriptions();

    const onConfirmAction = () => {
        if (
            year == null ||
            selectedBudgetProposals == null ||
            resourceSoftwareIds == null ||
            softwareConfig == null ||
            existingInscriptionsAction == null ||
            resetAmountAndCommentAction == null ||
            inscriptionSource == null ||
            (inscriptionSource.value ==
                InscriptionSource.BudgetLineInscriptions &&
                ((softwareConfig.value.showCommentColumn &&
                    commentColumn == null) ||
                    inscriptionSteps == null)) ||
            (inscriptionSource.value == InscriptionSource.PhaseInscriptions &&
                (phase == null || keepProjectAction == null))
        )
            return;
        trackEventConfirmInitializeInscriptions();
        initInscriptions({
            software: softwareConfig.value.software,
            inscriptionSource: inscriptionSource.value,
            year: year.value,
            budgetProposalIds: selectedBudgetProposals.map((e) => e.value.id),
            resources: resourceSoftwareIds.map((e) => e.value),
            keepInscription: existingInscriptionsAction.value,
            resetInscriptionOption: resetAmountAndCommentAction.value,
            step: inscriptionSteps?.map((x) => x.value),
            phase: phase?.value,
            commentColumn: commentColumn?.value,
            keepProject: keepProjectAction?.value,
        });
    };

    const steps = [
        {
            confirmAction: {
                enabled: softwareConfig !== null && inscriptionSource !== null,
                label: 'common.next',
            },
            stepBackAction: {
                onClick: () => {
                    setShowModal(false);
                },
                enabled: true,
            },
            disableClose: true,
            node: (
                <FirstStep
                    softwareConfig={softwareConfig}
                    setSoftwareConfig={setSoftwareConfig}
                    availableSoftwaresConfigs={availableSoftwaresConfigs}
                    inscriptionSource={inscriptionSource}
                    setInscriptionSource={setInscriptionSource}
                    availableInscriptionSources={availableInscriptionSources}
                    currentStep={1}
                />
            ),
        },
        {
            confirmAction: {
                enabled:
                    year !== null &&
                    ((inscriptionSteps !== null &&
                        inscriptionSteps.length > 0) ||
                        phase !== null),
                label: 'common.next',
            },
            stepBackAction: {
                enabled: true,
            },
            disableClose: true,
            node: (
                <SecondStep
                    year={year}
                    setYear={setYear}
                    availableYears={availableYears}
                    step={inscriptionSteps}
                    setStep={setInscriptionSteps}
                    availableSteps={availableSteps}
                    phase={phase}
                    setPhase={setPhase}
                    availablePhases={availablePhases}
                    inscriptionSource={inscriptionSource}
                    currentStep={2}
                />
            ),
        },
        {
            confirmAction: {
                enabled:
                    selectedBudgetProposals !== null &&
                    selectedBudgetProposals.length > 0 &&
                    resourceSoftwareIds !== null &&
                    resourceSoftwareIds.length > 0,
                label: 'common.next',
            },
            stepBackAction: {
                enabled: true,
            },
            disableClose: true,
            node: (
                <ThirdStep
                    selectedBudgetProposals={selectedBudgetProposals}
                    setSelectedBudgetProposals={setSelectedBudgetProposals}
                    resourceSoftwareIds={resourceSoftwareIds}
                    setResourceSoftwareIds={setResourceSoftwareIds}
                    availableBudgetProposals={budgetProposals}
                    inscriptionSource={inscriptionSource}
                    currentStep={3}
                />
            ),
        },
        {
            confirmAction: {
                enabled:
                    existingInscriptionsAction !== null &&
                    resetAmountAndCommentAction !== null &&
                    (commentColumn !== null ||
                        !softwareConfig?.value.showCommentColumn ||
                        keepProjectAction !== null),
                label: 'common.next',
            },
            stepBackAction: {
                enabled: true,
            },
            disableClose: true,
            node: (
                <FourthStep
                    existingInscriptionsAction={existingInscriptionsAction}
                    setExistingInscriptionsAction={
                        setExistingInscriptionsAction
                    }
                    availableExistingInscriptionsActions={
                        availableExistingInscriptionsActions
                    }
                    resetAmountAndCommentAction={resetAmountAndCommentAction}
                    setResetAmountAndCommentAction={
                        setResetAmountAndCommentAction
                    }
                    availableResetAmountAndCommentActions={
                        availableResetAmountAndCommentActions
                    }
                    commentColumn={commentColumn}
                    setCommentColumn={setCommentColumn}
                    availableCommentColumns={availableCommentColumns}
                    keepProjectAction={keepProjectAction}
                    setKeepProjectAction={setKeepProjectAction}
                    availableKeepProjectActions={availableKeepProjectActions}
                    softwareConfig={softwareConfig}
                    inscriptionSource={inscriptionSource}
                    currentStep={4}
                />
            ),
        },
        {
            confirmAction: {
                enabled: confirmationIsValid,
                onClick: onConfirmAction,
                label: 'common.confirm',
            },
            stepBackAction: {
                enabled: true,
            },
            disableClose: true,
            node: (
                <FifthStep
                    existingInscriptionsAction={existingInscriptionsAction}
                    selectedBudgetProposals={selectedBudgetProposals}
                    confirmationIsValid={confirmationIsValid}
                    setConfirmationIsValid={setConfirmationIsValid}
                />
            ),
        },
    ];
    return (
        <StyMultiStepModal
            isOpen={isModalShown}
            onClose={() => {
                setShowModal(false);
            }}
            maxWidth="md"
            steps={steps}
            showCloseIcon={true}
        />
    );
}
